import moment from 'moment'
import { ExpirationScheduleEntry, Transaction } from '@/types/transaction'

/**
 * The earliest expiration date the UI ever shows (YYYY-MM-DD). Every reward
 * expires 1 year after it was granted, so rewards from 2025 have real
 * `expires_at` dates that are already past or fall before this date. Nothing
 * actually expires until the backend turns expiration on (planned for this
 * date), so earlier dates are shown as this one and merged into one bucket.
 * Safe to remove once every real expiration date is on or after it.
 */
export const REWARD_EXPIRATION_MIN_DISPLAY_DATE = '2026-10-20'

/**
 * The "You have X pts expiring" banner only covers points that expire within
 * this window (Figma decision, Oct 1 2026)
 */
export const EXPIRING_BANNER_WINDOW: moment.DurationInputObject = { days: 30 }

/** The Points Expiring Soon card lists dates within this window */
export const EXPIRING_SOON_CARD_WINDOW: moment.DurationInputObject = {
  months: 3,
}

export const REWARD_DATE_FORMAT = 'MMM DD, YYYY'

const DAY_FORMAT = 'YYYY-MM-DD'

/** A date or ISO datetime as a local YYYY-MM-DD day */
function toDay(date: string | Date): string {
  return moment(date).format(DAY_FORMAT)
}

/** The date to show for an expiration: never earlier than the minimum */
export function clampExpirationDate(
  date: string,
  minDate: string = REWARD_EXPIRATION_MIN_DISPLAY_DATE,
): string {
  const day = toDay(date)
  return day < minDate ? minDate : day
}

/**
 * Clamps every schedule date to the minimum display date, summing the points
 * of entries that land on the same day (everything before the minimum merges
 * into one bucket on it). Returned ascending by date.
 */
export function mergeExpirationSchedule(
  schedule: ExpirationScheduleEntry[] | null | undefined,
  minDate: string = REWARD_EXPIRATION_MIN_DISPLAY_DATE,
): ExpirationScheduleEntry[] {
  const pointsByDay = new Map<string, number>()
  for (const entry of schedule ?? []) {
    if (entry.points <= 0) continue
    const day = clampExpirationDate(entry.expires_at, minDate)
    pointsByDay.set(day, (pointsByDay.get(day) ?? 0) + entry.points)
  }
  return [...pointsByDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([expires_at, points]) => ({ expires_at, points }))
}

/**
 * The merged schedule limited to dates within `window` of `today`: the banner
 * window for the "You have X pts expiring" banner, the card window for the
 * Points Expiring Soon card.
 */
export function getExpiringSoonSchedule(
  schedule: ExpirationScheduleEntry[] | null | undefined,
  today: Date | string,
  window: moment.DurationInputObject,
  minDate: string = REWARD_EXPIRATION_MIN_DISPLAY_DATE,
): ExpirationScheduleEntry[] {
  const lastDay = moment(today).add(window).format(DAY_FORMAT)
  return mergeExpirationSchedule(schedule, minDate).filter(
    (entry) => entry.expires_at <= lastDay,
  )
}

/** Formats a date or ISO datetime for reward rows, e.g. `Oct 20, 2026` */
export function formatRewardDate(date: string): string {
  return moment(date).format(REWARD_DATE_FORMAT)
}

// Expirations run daily, so a regular one lands within a day or two of the
// credit's 1-year anniversary
const REGULAR_EXPIRATION_GRACE_DAYS = 2

/**
 * True for the one-time Oct 2026 expiration of points earned before the
 * rewards terms changed: at least one expired credit was earned more than a
 * year (plus a little slack) before the expiration. Those show legacy copy and
 * month-and-year earned dates.
 */
export function isLegacyExpiration(expiration: Transaction): boolean {
  const regularCutoff = moment(expiration.created)
    .subtract(1, 'year')
    .subtract(REGULAR_EXPIRATION_GRACE_DAYS, 'days')
  return (expiration.expired_breakdown ?? []).some(
    (item) => item.points > 0 && moment(item.created).isBefore(regularCutoff),
  )
}

/**
 * Converts points to dollars using the conversion rate of 100 points = $1
 * @param points - The number of points to convert
 * @returns The formatted dollar amount string (e.g., "5.00")
 */
export function pointsToDollars(points: number): string {
  const dollars = points / 100
  return dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/**
 * Machine-readable code from POST transactions/redeem when the reward provider
 * (Tremendous) couldn't complete the redemption. The backend rolled the
 * redemption back, so the user's points were not deducted.
 */
export const REWARD_PROVIDER_ERROR_CODE = 'reward_provider_error'

export const GENERIC_REDEMPTION_ERROR_MESSAGE =
  'There was an error redeeming your points. Please try again.'
export const PROVIDER_REDEMPTION_ERROR_MESSAGE =
  "We couldn't process your redemption. Your points are safe, so please try again in a few minutes."
export const AMBIGUOUS_REDEMPTION_ERROR_MESSAGE =
  "We couldn't confirm whether your redemption went through. Please check your recent activity before redeeming again."

/** The failure payload redux-api-middleware hands back for a failed request. */
export type RedemptionErrorPayload = {
  status?: number
  response?: unknown
}

export type ClassifiedRedemptionError = {
  message: string
  /**
   * True when there was no structured backend response (network drop, or a
   * gateway cut the connection): the server may still have completed the
   * redemption after the cutoff, so reconcile the activity list instead of
   * encouraging a blind retry.
   */
  ambiguousOutcome: boolean
}

export function classifyRedemptionError(
  payload?: RedemptionErrorPayload,
): ClassifiedRedemptionError {
  const response = payload?.response as { code?: unknown } | undefined
  if (
    response &&
    typeof response === 'object' &&
    response.code === REWARD_PROVIDER_ERROR_CODE
  ) {
    return {
      message: PROVIDER_REDEMPTION_ERROR_MESSAGE,
      ambiguousOutcome: false,
    }
  }

  // 400s and handled 500s both mean the backend refused or rolled back the
  // redemption, so points are safe and a retry is fine. The 400 body is a raw
  // request-validation error, not something the user typed, so it isn't shown.
  if (payload?.status === 400 || payload?.status === 500) {
    return {
      message: GENERIC_REDEMPTION_ERROR_MESSAGE,
      ambiguousOutcome: false,
    }
  }

  // No structured backend response: network drop, or a gateway cut the
  // connection (502/504/524) while the backend may have kept working
  return { message: AMBIGUOUS_REDEMPTION_ERROR_MESSAGE, ambiguousOutcome: true }
}
